import React, { useState } from 'react';

interface AiTranscribeAudioConfigProps {
  config: any;
  onChange: (key: string, value: any) => void;
  onBatchChange?: (changes: Record<string, any>) => void;
}

const KNOWN_MODELS = new Set<string>([
  'gpt-transcribe',
  'gpt-4o-mini-transcribe',
  'gpt-4o-transcribe',
  'whisper-1',
  'gemini-3.8-flash',
  'gemini-2.5-flash',
  'gemini-2.0-flash',
]);

export const AiTranscribeAudioConfig: React.FC<AiTranscribeAudioConfigProps> = ({ config, onChange, onBatchChange }) => {
  const provider = config.provider || 'openai';
  const [customModel, setCustomModel] = useState(false);
  const isCustomModel = customModel || (!!config.model && !KNOWN_MODELS.has(config.model));


  const handleProviderChange = (newProvider: string) => {
    const defaultModel = newProvider === 'gemini' ? 'gemini-2.0-flash' : 'gpt-transcribe';
    if (onBatchChange) {
      onBatchChange({ provider: newProvider, model: defaultModel });
    } else {
      onChange('provider', newProvider);
      onChange('model', defaultModel);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 leading-relaxed">
        <strong>Requisito obrigatório:</strong> este nó só funciona com conexão de IA ativa no aplicativo.
        Configure a chave em <strong>Configurações → Integrações</strong> (OpenAI ou Gemini).
      </div>

      <div className="bg-violet-50 border border-violet-200 rounded-lg p-3 text-xs text-violet-700 leading-relaxed">
        <strong>🎙️ Transcrição de áudio automática:</strong> converte o áudio recebido em texto usando IA.
        <br /><br />
        <strong>Entrada:</strong> usa automaticamente o áudio da mensagem do cliente + prompt opcional.
        <br />
        <strong>Saída:</strong> salva o resultado em <strong>audio_transcript</strong> para os próximos nós.
      </div>

      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2">
        <div>
          <p className="text-xs font-medium text-slate-700">Variável de entrada automática</p>
          <p className="text-xs text-slate-500 mt-0.5">Não precisa configurar: o sistema preenche sozinho quando o cliente envia áudio.</p>
          <p className="mt-1 text-xs font-mono text-slate-800">audio_file_url</p>
        </div>
        <div>
          <p className="text-xs font-medium text-slate-700">Variável de saída</p>
          <p className="mt-1 text-xs font-mono text-slate-800">audio_transcript</p>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">Provedor de IA</label>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => handleProviderChange('openai')}
            className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border-2 text-sm font-medium transition-all ${
              provider === 'openai'
                ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
            }`}
          >
            <img src="/providers/openai.svg" alt="OpenAI" className="h-5 w-auto" />
          </button>
          <button
            type="button"
            onClick={() => handleProviderChange('gemini')}
            className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border-2 text-sm font-medium transition-all ${
              provider === 'gemini'
                ? 'border-blue-500 bg-blue-50 text-blue-700'
                : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
            }`}
          >
            <img src="/providers/gemini.svg" alt="Google Gemini" className="h-5 w-auto" />
          </button>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Modelo</label>
        <select
          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={isCustomModel ? '__custom__' : (config.model || (provider === 'gemini' ? 'gemini-2.0-flash' : 'gpt-transcribe'))}
          onChange={(e) => {
            const value = e.target.value;
            if (value === '__custom__') {
              setCustomModel(true);
              return;
            }
            setCustomModel(false);
            onChange('model', value);
          }}
        >
          {provider === 'openai' ? (
            <>
              <option value="gpt-transcribe">GPT Transcribe — máxima precisão ✓ recomendado</option>
              <option value="gpt-4o-mini-transcribe">GPT-4o Mini Transcribe — descontinuado em 26/02/2027</option>
              <option value="gpt-4o-transcribe">GPT-4o Transcribe — descontinuado em 26/02/2027</option>
              <option value="whisper-1">Whisper-1 — descontinuado em 26/02/2027</option>
            </>
          ) : (
            <>
              <option value="gemini-3.8-flash">Gemini 3.8 Flash — mais recente ✓ recomendado</option>
              <option value="gemini-2.5-flash">Gemini 2.5 Flash — rápido</option>
              <option value="gemini-2.0-flash">Gemini 2.0 Flash — geração anterior</option>
            </>
          )}
          <option value="__custom__">✏️ Outro modelo — digitar o ID…</option>
        </select>
        {isCustomModel && (
          <input
            type="text"
            className="w-full mt-2 px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="ID exato do modelo (ex.: gpt-5.3-luna)"
            value={config.model || ''}
            onChange={(e) => onChange('model', e.target.value.trim())}
            autoFocus
          />
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">Prompt de transcrição (opcional)</label>
        <textarea
          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          rows={3}
          placeholder="Ex: Transcreva fielmente e mantenha nomes próprios sem corrigir."
          value={config.prompt || ''}
          onChange={(e) => onChange('prompt', e.target.value)}
        />
      </div>
    </div>
  );
};
